// src/lib/privacy/privacyRequestApi.ts

import { db } from '../../firebase/config';
import { 
  collection, 
  getDocs, 
  getDoc, 
  doc, 
  setDoc, 
  updateDoc, 
  query, 
  where, 
  orderBy,
  Timestamp 
} from 'firebase/firestore';
import { PrivacyRequest, UserPrivacyPreferences } from '../../types/privacyRequest';
import { 
  sendNewPrivacyRequestNotification, 
  sendPrivacyRequestStatusNotification 
} from '../email/emailService';
import { logPrivacyEvent } from '../audit/auditLogger';

/**
 * Creates a new privacy request
 */
export async function createPrivacyRequest(
  userId: string,
  email: string,
  requestType: PrivacyRequest['requestType'],
  description?: string
): Promise<string> {
  const requestsRef = collection(db, 'privacyRequests');
  const requestId = doc(requestsRef).id;
  
  const now = Timestamp.now();
  const deadline = new Date(now.toDate());
  deadline.setDate(deadline.getDate() + 45); // 45 days deadline
  
  const newRequest: any = {
    userId,
    email,
    requestType,
    status: 'pending',
    verificationMethod: 'authenticated', // Since user is logged in
    submittedAt: now,
    deadline: Timestamp.fromDate(deadline),
    createdAt: now,
    updatedAt: now
  };
  
  // Only include description if it's provided (Firestore doesn't allow undefined)
  if (description && description.trim() !== '') {
    newRequest.description = description.trim();
  }
  
  await setDoc(doc(db, 'privacyRequests', requestId), newRequest);
  
  // Get user role for audit logging
  let userRole: 'admin' | 'staff' | 'client' = 'client';
  try {
    const userDocRef = doc(db, 'users', userId);
    const userDoc = await getDoc(userDocRef);
    if (userDoc.exists()) {
      userRole = (userDoc.data().role || 'client') as 'admin' | 'staff' | 'client';
    }
  } catch (err) {
    console.warn('Could not get user role for audit logging:', err);
  }
  
  // Log privacy request submission
  logPrivacyEvent(
    userId,
    email,
    userRole,
    'privacy_request_submitted',
    'privacy_request',
    requestId,
    `Privacy request submitted: ${requestType}`,
    { requestType, hasDescription: !!description }
  ).catch((err: any) => console.error('Failed to log privacy request submission:', err));
  
  // Send email notification to admin
  sendNewPrivacyRequestNotification(requestType, email, requestId, description).catch((err: any) => {
    console.error('Failed to send new privacy request notification:', err);
  });
  
  return requestId;
}

/**
 * Gets all privacy requests for a user
 */
export async function getUserPrivacyRequests(userId: string): Promise<PrivacyRequest[]> {
  const requestsRef = collection(db, 'privacyRequests');
  // For client queries, filter by userId to ensure they only see their own
  const requestsQuery = query(
    requestsRef,
    where('userId', '==', userId),
    orderBy('submittedAt', 'desc')
  );
  
  const snapshot = await getDocs(requestsQuery);
  const requests: PrivacyRequest[] = [];
  
  snapshot.forEach((docSnap) => {
    requests.push({
      id: docSnap.id,
      ...docSnap.data()
    } as PrivacyRequest);
  });
  
  return requests;
}

/**
 * Gets a single privacy request by ID
 */
export async function getPrivacyRequest(requestId: string): Promise<PrivacyRequest | null> {
  const requestDocRef = doc(db, 'privacyRequests', requestId);
  const requestDocSnap = await getDoc(requestDocRef);
  
  if (!requestDocSnap.exists()) {
    return null;
  }
  
  return {
    id: requestDocSnap.id,
    ...requestDocSnap.data()
  } as PrivacyRequest;
}

/**
 * Gets all privacy requests (admin only)
 */
export async function getAllPrivacyRequests(): Promise<PrivacyRequest[]> {
  const requestsRef = collection(db, 'privacyRequests');
  const requestsQuery = query(requestsRef, orderBy('submittedAt', 'desc'));
  
  const snapshot = await getDocs(requestsQuery);
  const requests: PrivacyRequest[] = [];
  
  snapshot.forEach((docSnap) => {
    requests.push({
      id: docSnap.id,
      ...docSnap.data()
    } as PrivacyRequest);
  });
  
  return requests;
}

/**
 * Updates a privacy request status
 */
export async function updatePrivacyRequestStatus(
  requestId: string,
  status: PrivacyRequest['status'],
  processedBy?: string,
  notes?: string,
  exportedDataUrl?: string,
  deletionResult?: {
    deletedCollections: string[];
    deletionExceptions: string[];
    softDeleted: boolean;
  }
): Promise<void> {
  const requestDocRef = doc(db, 'privacyRequests', requestId);
  const updateData: any = {
    status,
    updatedAt: Timestamp.now()
  };
  
  if (processedBy) {
    updateData.processedBy = processedBy;
    updateData.processedAt = Timestamp.now();
  }
  
  if (notes) {
    const request = await getPrivacyRequest(requestId);
    const existingNotes = request?.notes || [];
    updateData.notes = [...existingNotes, notes];
  }
  
  if (exportedDataUrl) {
    updateData.exportedDataUrl = exportedDataUrl;
    updateData.dataExportedAt = Timestamp.now();
  }
  
  if (deletionResult) {
    updateData.deletedCollections = deletionResult.deletedCollections;
    updateData.deletionExceptions = deletionResult.deletionExceptions;
    updateData.softDeleted = deletionResult.softDeleted;
  }
  
  await updateDoc(requestDocRef, updateData);
  
  // Get the updated request to send email notification
  const updatedRequest = await getPrivacyRequest(requestId);
  if (updatedRequest) {
    // Send email notifications (to client and admin)
    sendPrivacyRequestStatusNotification(
      updatedRequest.requestType,
      status,
      updatedRequest.email,
      requestId,
      updatedRequest.description,
      notes
    ).catch((err: any) => {
      console.error('Failed to send privacy request status notification:', err);
    });
  }
}

/**
 * Verifies a privacy request
 */
export async function verifyPrivacyRequest(
  requestId: string,
  verificationMethod: PrivacyRequest['verificationMethod'] = 'email'
): Promise<void> {
  const requestDocRef = doc(db, 'privacyRequests', requestId);
  await updateDoc(requestDocRef, {
    status: 'in_progress',
    verificationMethod,
    verifiedAt: Timestamp.now(),
    updatedAt: Timestamp.now()
  });
  
  // Get the request to send email notification
  const request = await getPrivacyRequest(requestId);
  if (request) {
    sendPrivacyRequestStatusNotification(
      request.requestType,
      'in_progress',
      request.email,
      requestId,
      request.description
    ).catch((err: any) => {
      console.error('Failed to send privacy request verification notification:', err);
    });
  }
}

/**
 * Gets or creates user privacy preferences
 */
export async function getUserPrivacyPreferences(userId: string): Promise<UserPrivacyPreferences | null> {
  const prefsDocRef = doc(db, 'userPrivacyPreferences', userId);
  const prefsDocSnap = await getDoc(prefsDocRef);
  
  if (!prefsDocSnap.exists()) {
    return null;
  }
  
  return prefsDocSnap.data() as UserPrivacyPreferences;
}

/**
 * Updates user privacy preferences
 */
export async function updateUserPrivacyPreferences(
  userId: string,
  preferences: Partial<Omit<UserPrivacyPreferences, 'userId' | 'updatedAt'>>
): Promise<void> {
  const prefsDocRef = doc(db, 'userPrivacyPreferences', userId);
  const prefsDocSnap = await getDoc(prefsDocRef);
  
  const updateData: Partial<UserPrivacyPreferences> = {
    ...preferences,
    updatedAt: Timestamp.now()
  };
  
  if (!prefsDocSnap.exists()) {
    // Create new preferences document
    await setDoc(prefsDocRef, {
      userId,
      optOutSale: false,
      optOutSharing: false,
      optOutMarketing: false,
      limitSensitiveInfo: false,
      ...updateData
    });
  } else {
    // Update existing preferences
    await updateDoc(prefsDocRef, updateData);
  }
}
